import {
  createTokenManager,
  setAuthTokenGetter,
  useGetMe,
  getGetMeQueryKey,
} from "@workspace/api-client";
import type { UserProfile } from "@workspace/api-client";
import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";

export const tokenManager = createTokenManager({
  accessTokenKey: "accessToken",
  refreshTokenKey: "refreshToken",
});

// Wire the token getter into the API client on module load.
setAuthTokenGetter(tokenManager.getValidToken);

export function logout(): void {
  // Fire-and-forget: tell the server to blacklist the current access token
  // and clear the HttpOnly refresh-token cookie.
  const token = localStorage.getItem("accessToken");
  if (token) {
    fetch("/api/auth/logout", {
      method:      "POST",
      credentials: "include",
      headers:     { Authorization: `Bearer ${token}` },
    }).catch(() => { /* best-effort; local state is cleared regardless */ });
  }
  tokenManager.clearTokens();
  localStorage.removeItem("_dev_user");
}

function getDevUser(): UserProfile | null {
  if (!import.meta.env.DEV) return null;
  try {
    const raw = localStorage.getItem("_dev_user");
    return raw ? (JSON.parse(raw) as UserProfile) : null;
  } catch {
    return null;
  }
}

export function useAuth() {
  const devUser = getDevUser();
  const hasSession = tokenManager.hasSession();
  const isDevBypass = devUser !== null && hasSession;

  if (devUser && !hasSession) {
    localStorage.removeItem("_dev_user");
  }

  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(
    hasSession,
  );
  const navigate = useNavigate();

  // Only call /auth/me when NOT in dev bypass mode
  const { data: apiUser, isLoading, isError } = useGetMe({
    query: {
      enabled: isAuthenticated && !isDevBypass,
      retry: false,
      queryKey: getGetMeQueryKey(),
    },
  });

  const user = isDevBypass ? devUser : apiUser;

  const handleLogout = useCallback(() => {
    logout();
    setIsAuthenticated(false);
    navigate("/login");
  }, [navigate]);

  useEffect(() => {
    if (isError) handleLogout();
  }, [isError, handleLogout]);

  return {
    user,
    isLoading: isDevBypass ? false : isLoading,
    isAuthenticated,
    logout: handleLogout,
  };
}
